import { Icon } from "@circle-react-shared/Icon";
import { popOverInOut } from "@circle-react-shared/uikit/animations";
import type {
  HeaderNavItemKind,
  HeaderNavItemVisibility,
} from "@circle-react/components/SettingsApp/Navigation/hooks/useFetchHeaderNavItems";
import { useSidebarBorderStyles } from "@circle-react/components/Spaces/SidebarV2/hooks";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Button } from "@circleco/compass/components/Button";
import { Popover as CompassPopover } from "@circleco/compass/components/Popover";
import { Popover as HeadlessPopover, Transition } from "@headlessui/react";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

import { NavigationLink } from "../NavigationItem";

const i18nRoot = "header_v3.navigation";

interface Item {
  kind: HeaderNavItemKind;
  label: string;
  linkTo: string;
  openInNewWindow?: boolean;
  visibility: HeaderNavItemVisibility;
}
export interface MorePopoverProps {
  isPreviewMode?: boolean;
  items: Item[];
}

export const MorePopover = ({
  items,
  isPreviewMode = false,
}: MorePopoverProps) => {
  const { borderColor } = useSidebarBorderStyles();
  const isCompassEnabled = useIsCircleV4FeatureEnabled(
    "circle_v4_popover_enabled",
  );
  const isCompassButtonsEnabled = useIsCircleV4FeatureEnabled(
    "circle_v4_buttons_enabled",
  );
  const [isOpen, setIsOpen] = useState(false);

  // Legacy path (Headless UI)
  if (!isCompassEnabled) {
    return (
      <HeadlessPopover className="relative">
        {({ open, close }) => (
          <>
            <HeadlessPopover.Button
              disabled={isPreviewMode}
              className={classNames(
                "text-sm h-8.5 focus-visible:outline-secondary flex items-center !rounded-full py-2 pl-4 pr-2 font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                "hover:bg-c-header-hover text-c-header hover:text-c-header-active focus:text-c-header-active focus:bg-c-header-hover",
                {
                  "bg-c-header-hover text-c-header-active": open,
                },
              )}
            >
              <span className="flex gap-0.5">
                {t([i18nRoot, "more"])}
                <Icon
                  type="12-chevron-down-v3"
                  className="!transition-none"
                  size={12}
                />
              </span>
            </HeadlessPopover.Button>
            <Transition {...popOverInOut}>
              <HeadlessPopover.Panel
                className="bg-c-header absolute right-0 top-1.5 flex w-max flex-col rounded-xl border p-4 shadow-lg"
                style={{ borderColor }}
              >
                {items.map(
                  ({ linkTo, label, kind, visibility, openInNewWindow }) => (
                    <NavigationLink
                      key={linkTo}
                      label={label}
                      linkTo={linkTo}
                      isFullRounded={false}
                      isActive={false}
                      onClick={close}
                      kind={kind}
                      visibility={visibility}
                      openInNewWindow={openInNewWindow}
                    />
                  ),
                )}
              </HeadlessPopover.Panel>
            </Transition>
          </>
        )}
      </HeadlessPopover>
    );
  }

  // New path (Compass Popover)
  const trigger = isCompassButtonsEnabled ? (
    <Button
      variant="ghost"
      size="sm"
      endIcon="chevron-down"
      disabled={isPreviewMode}
    >
      {t([i18nRoot, "more"])}
    </Button>
  ) : (
    <button
      type="button"
      disabled={isPreviewMode}
      className={classNames(
        "text-sm h-8.5 focus-visible:outline-secondary flex items-center !rounded-full py-2 pl-4 pr-2 font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        "hover:bg-c-header-hover text-c-header hover:text-c-header-active focus:text-c-header-active focus:bg-c-header-hover",
        {
          "bg-c-header-hover text-c-header-active": isOpen,
        },
      )}
    >
      <span className="flex gap-0.5">
        {t([i18nRoot, "more"])}
        <Icon
          type="12-chevron-down-v3"
          className="!transition-none"
          size={12}
        />
      </span>
    </button>
  );

  return (
    <div className="relative">
      <CompassPopover
        trigger={trigger}
        open={isOpen}
        onOpenChange={setIsOpen}
        side="bottom"
        align="end"
      >
        <div
          className="bg-c-header flex w-max flex-col rounded-xl border p-4 shadow-lg"
          style={{ borderColor }}
        >
          {items.map(({ linkTo, label, kind, visibility, openInNewWindow }) => (
            <NavigationLink
              key={linkTo}
              label={label}
              linkTo={linkTo}
              isFullRounded={false}
              isActive={false}
              onClick={() => setIsOpen(false)}
              kind={kind}
              visibility={visibility}
              openInNewWindow={openInNewWindow}
            />
          ))}
        </div>
      </CompassPopover>
    </div>
  );
};
